import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const EMPTY = { name: "", type: "property_management_company", email: "", phone: "", website: "", primary_contact_name: "", referral_status: "unknown", referral_terms: "", notes: "" };

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(null);
  const { user } = useAuth();

  const load = () => api.get("/admin/companies").then((r) => setCompanies(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (form.company_id) await api.put(`/admin/companies/${form.company_id}`, form);
      else await api.post("/admin/companies", form);
      setForm(null); load(); toast.success("Saved");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (cid) => {
    if (!window.confirm("Delete this company? Listings keep their company name text.")) return;
    try { await api.delete(`/admin/companies/${cid}`); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="companies-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">Companies & landlords</h1>
          <p className="text-sm text-muted-foreground">{companies.length} records</p>
        </div>
        <button data-testid="company-add" onClick={() => setForm(EMPTY)}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold"><Plus className="w-4 h-4" /> Add company</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {companies.map((c) => (
          <div key={c.company_id} data-testid={`company-card-${c.company_id}`} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h2 className="font-semibold">{c.name}</h2>
                <p className="text-xs text-muted-foreground capitalize">{c.type.replace(/_/g, " ")} · {c.listings_count} listings</p>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${c.referral_status === "partner" ? "bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-100" : "bg-secondary"}`}>{c.referral_status.replace(/_/g, " ")}</span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
            {c.referral_terms && <p className="mt-2 rounded-lg bg-secondary px-3 py-2 text-xs"><span className="font-semibold">Terms:</span> {c.referral_terms}</p>}
            <div className="mt-3 flex gap-2">
              <button data-testid={`company-edit-${c.company_id}`} onClick={() => setForm(c)} className="rounded-full border border-border px-4 py-1.5 text-xs font-semibold">Edit</button>
              {user?.role === "admin" && (
                <button data-testid={`company-delete-${c.company_id}`} onClick={() => remove(c.company_id)} aria-label="Delete company"
                  className="p-1.5 rounded-lg text-coral-600 hover:bg-coral-50"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          </div>
        ))}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Company form">
          <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 max-h-[85vh] overflow-y-auto">
            <h2 className="font-heading font-semibold text-xl">{form.company_id ? "Edit company" : "Add company"}</h2>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {[["Name *", "name"], ["Contact name", "primary_contact_name"], ["Email", "email"], ["Phone", "phone"], ["Website", "website"], ["Referral terms", "referral_terms"], ["Notes", "notes"]].map(([label, k]) => (
                <div key={k} className={k === "notes" || k === "referral_terms" ? "col-span-2" : ""}>
                  <label className="block text-xs font-semibold mb-1">{label}</label>
                  <input data-testid={`company-form-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm" />
                </div>
              ))}
              <div>
                <label className="block text-xs font-semibold mb-1">Type</label>
                <select data-testid="company-form-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm">
                  <option value="property_management_company">Property management</option>
                  <option value="individual_landlord">Individual landlord</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Referral status</label>
                <select data-testid="company-form-referral" value={form.referral_status} onChange={(e) => setForm({ ...form, referral_status: e.target.value })}
                  className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm">
                  {["unknown", "has_referral_program", "no_referral_program", "in_discussion", "partner"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button data-testid="company-form-cancel" onClick={() => setForm(null)} className="rounded-full border border-border px-5 py-2 text-sm font-medium">Cancel</button>
              <button data-testid="company-form-save" onClick={save} className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
